import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import shareIcon from "../../../assets/images/share.png";
import closeIcon from "../../../assets/images/close.png";
import arrowIconWhite from "../../../assets/images/arrowIconWhite.png";
import Illustrazione from "../../../assets/images/icona-pag7.svg";

/* ===================== DATA ===================== */
const KPI = [
  { id: "utile",        label: "Consolidated profit",                              value: 321.6,   unit: "eur", note: "million", decimals: 1 },
  { id: "solvency",     label: "Solvency II ratio (*) (**)",                       value: 337.8,   unit: "pct", note: null,      decimals: 1 },
  { id: "passivita",    label: "Net technical provisions",                         value: 17926.1, unit: "eur", note: "million", decimals: 1 },
  { id: "premi",        label: "Premiums written",                                 value: 6624.7,  unit: "eur", note: "million", decimals: 1 },

  { id: "investimenti", label: "Investments",                                      value: 21674.5, unit: "eur", note: "million", decimals: 1 },
  { id: "cro",          label: "Combined Operating Ratio Non-Life (**)",           value: 91.7,    unit: "pct", note: null,      decimals: 1 },
  { id: "ris_vita",     label: "Life Operating Result related to volumes managed (**)", value: 0.75, unit: "pct", note: null,   decimals: 2 },
  { id: "csm",          label: "CSM Life/Liability for remaining coverage (**)",   value: 6.2,     unit: "pct", note: null,      decimals: 1 },

  { id: "attivo",       label: "Total assets",                                     value: 25019.5, unit: "eur", note: "million", decimals: 1 },
  { id: "patrimonio",   label: "Consolidated equity",                              value: 3996.2,  unit: "eur", note: "million", decimals: 1 },
];

/* ===================== COUNTER ===================== */
function useInView(ref, rootMargin = "0px") {
  const [inView, set] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && set(true),
      { root: null, rootMargin, threshold: 0.2 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref, rootMargin]);
  return inView;
}

function formatNumber(val, decimals, unit) {
  if (val == null) return "—";
  const n = Number(val);
  const fmt = n.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: "always",
  });
  if (unit === "pct") return `${fmt}%`;
  return fmt;
}

const onShare = (popupId) => {
    const url = new URL(window.location.href);
    url.searchParams.set("popup", popupId);

    if (navigator.share) {
      navigator
        .share({ title: "Income and financial position", url: url.toString() })
        .catch(() => {});
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(url.toString()).catch(() => {});
    }
  };


function Counter({ value, decimals = 1, unit = "eur", duration = 1200, className = "", note }) {
  const ref = useRef(null);
  const inView = useInView(ref, "0px");
  const [display, setDisplay] = useState(value == null ? "—" : formatNumber(0, decimals, unit));

  useEffect(() => {
    if (!inView || value == null) return;
    let raf, start;
    const from = 0;
    const to = Number(value);
    const easeOut = (t) => 1 - Math.pow(1 - t, 3);
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min(1, (ts - start) / duration);
      const eased = easeOut(p);
      const curr = from + (to - from) * eased;
      setDisplay(formatNumber(curr, decimals, unit));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, decimals, unit, duration]);


  return (
    <>
      <div>
        <div ref={ref} className={className}>{display}</div>
        <div style={!note ? { height: "20px" } : {}}>{note}</div>
      </div>
    </>
  )
}

/* ===================== SUB-COMPONENTS ===================== */
function KpiCard({ kpi }) {
  return (
    <div className="flex flex-col justify-between h-[100%]">
      <div className="text-[#0E3761] text-[16px] md:text-[26px] leading-snug font-semibold">
        {kpi.label}
      </div>
      <Counter
        value={kpi.value}
        decimals={kpi.decimals ?? 1}
        unit={kpi.unit}
        className="text-[#0961C9] text-[50px] md:text-[34px] font-bold mb-auto"
        note={kpi.note}
      />
    </div>
  );
}

/* ===================== MAIN POPUP ===================== */
export default function PopupEconomicoPatrimoniale({ isOpen, onClose }) {
  const overlayRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    if (isOpen) {
      document.body.classList.add("overflow-hidden");
      window.addEventListener("keydown", onKey);
    }
    return () => {
      document.body.classList.remove("overflow-hidden");
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[999] bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-3 md:p-6"
      onMouseDown={(e) => { if (e.target === overlayRef.current) onClose?.(); }}
      role="dialog"
      aria-modal="true"
      aria-label="Our figures — Income and financial position"
    >
      <div
        className="relative w-full md:w-[1280px] shadow-2xl max-h-[90vh] overflow-y-auto overscroll-contain"
        style={{ background: "#F1F7FD" }}
      >
        {/* Header (share/close) */}
        <div className="sticky top-3 z-10 flex items-center gap-2 justify-end pr-3">
          <button
            onClick={() => onShare("PopupEconomicoPatrimoniale")}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/90 border border-[#0E3761]/30 hover:bg-white transition"
            title="Condividi" aria-label="Condividi"
          >
            <img src={shareIcon} alt="" className="h-4 w-4" />
          </button>
          <button
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/90 border border-[#0E3761]/30 hover:bg-white transition"
            title="Chiudi" aria-label="Chiudi"
          >
            <img src={closeIcon} alt="Chiudi" className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-8">
          <h2 className="text-[#0E3761] text-[30px] md:text-[40px] font-bold">Our figures</h2>
          <p className="text-[#0E3761] text-[18px] md:text-[22px] mt-1 mb-0">Income and financial position</p>
          <p className="text-[#0E3761] text-[12px] md:text-[14px] mt-0 italic">*As at 31st December 2025</p>

          {/* DESKTOP */}
          <div className="hidden lg:block mt-8">
            {/* card bianca */}
            <div className="bg-white rounded-[32px] p-10 border-2 border-[#b1c4e8] overflow-hidden">
              {/* layout 2 colonne: illustrazione + contenuti */}
              <div className="flex gap-10">
                {/* sinistra: testo sopra + blob dietro l'illustrazione */}
                <div className="relative w-1/4 min-w-[280px] flex flex-col mt-0 md:mt-[200px] md:ml-[-50px]">
                <div className="absolute top-[-80px] -left-[160px] w-[400px] h-[500px] rounded-full bg-[rgba(100,200,245,0.3)] z-0" />
                    <div className="flex flex-col justify-center items-center z-[1]">
                      <div className="text-[#0E3761] font-bold text-[22px] leading-snug mb-0">
                      Financial<br />solidity<br />of Reale Group
                    </div>
                      <img src={Illustrazione} alt="" className="relative z-[1] w-[100%]" />
                    </div>
                </div>

                {/* destra: KPI in grid */}
                <div className="w-3/4 flex flex-col gap-8">

                  {/* 1ª + 2ª riga: 4 colonne */}
                  <div className="grid grid-cols-4 gap-8">
                    {KPI.slice(0, 8).map(k => (
                      <div key={k.id}>
                        <KpiCard kpi={k} />
                      </div>
                    ))}
                  </div>

                  {/* separatore */}
                  <div className="h-px bg-[#0E3761]/20" />

                  {/* riga finale: 3 colonne → 2 KPI + box Nota */}
                  <div className="grid grid-cols-3 gap-8">
                    {KPI.slice(8).map(k => (
                      <div key={k.id}>
                        <KpiCard kpi={k} />
                      </div>
                    ))}

                    {/* box Nota come terza card */}
                    <div className="flex flex-col justify-end">
                      <div className="text-[12px] text-[#0E3761] opacity-70 leading-snug">
                        (*) The figure at 31 December 2025 refers to the fourth quarter<br />
                        (**) Variation expressed as a percentage
                      </div>
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="mt-2">
                    <a href="https://www.realegroup.eu/groupshare/0/EN/ListaReportsAndAccounts/64/Reale%20Group_Integrated%20Consolidated%20Financial%20Statement%202025.pdf#page=23" target="_blank">
                      <button
                        className="bt-secondary2 text-sm px-4 py-2"
                      >
                        <img src={arrowIconWhite} alt="Arrow" className="inline-block mr-2 w-[20px] h-[20px]" />
                        Consult the Report - chapter 1 • par. 1.1.3.3
                      </button>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>


          {/* MOBILE / TABLET */}
          <div className="block lg:hidden mt-4">
            <div className="bg-white rounded-[24px] p-6">
              {/* testo sopra + blob dietro l'immagine */}
              <div className="mb-8">
                <div className="text-[#0E3761] font-bold text-[18px] leading-snug mb-4">
                  Financial solidity of Reale Group
                </div>
                <div className="relative inline-flex justify-center items-center">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[160px] h-[160px] rounded-full bg-[rgba(100,200,245,0.3)] z-0" />
                  <img src={Illustrazione} alt="" className="relative z-[1] w-[200px]" />
                </div>
              </div>

              {/* due colonne con flex wrap */}
              <div className="flex flex-wrap gap-x-6 gap-y-6">
                {KPI.map(k => (
                  <div key={k.id} className="basis-1/2 min-w-[220px] flex-1">
                    <div className="text-[#0E3761] text-[20px] font-semibold leading-snug">{k.label}</div>
                    <Counter
                      value={k.value}
                      decimals={k.decimals ?? (k.unit === "pct" ? 1 : 1)}
                      unit={k.unit}
                      duration={900}
                      className="text-[#0961C9] text-[28px] font-bold"
                      note={k.note}
                    />
                    {k.note && <div className="text-[#0E3761] text-[12px] opacity-70"></div>}
                  </div>
                ))}
              </div>

              {/* note */}
              <div className="text-[12px] text-[#0E3761] opacity-70 leading-snug mt-4">
                (*) The figure at 31 December 2025 refers to the fourth quarter<br />
                (**) Variation expressed as a percentage
              </div>

              {/* CTA */}
              <div className="mt-6 flex justify-center">
                <a href="https://www.realegroup.eu/groupshare/0/EN/ListaReportsAndAccounts/64/Reale%20Group_Integrated%20Consolidated%20Financial%20Statement%202025.pdf#page=23" target="_blank">
                  <button
                    className="bt-secondary2 text-sm px-4 py-2"
                  >
                    <img src={arrowIconWhite} alt="Arrow" className="inline-block mr-2 w-[20px] h-[20px]" />
                    Consult the Report - chapter 1 • par. 1.1.3.3
                  </button>
                </a>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>,
    document.body
  );
}
